import { useEffect, useRef, useState } from "react";

/**
 * HID barcode scanner hook.
 *
 * Most USB / Bluetooth barcode scanners present as HID keyboards: each scan
 * is a sequence of keypresses ending with Enter (\n). This hook listens
 * globally for keydown events, buffers characters between non-printables,
 * and fires `onScan(text)` when Enter terminates the burst.
 *
 * Heuristic for distinguishing a scan from human typing:
 *   - Scanners burst keys faster than humans can type. We require ≥3 chars
 *     within 80ms of the previous one.
 *   - This is intentionally permissive: a 2-char scan would be a misread
 *     anyway, and the qty=1 default + line-quantity guard means a stray
 *     human keystroke can't double-fulfil anything.
 *
 * The hook is suspended whenever an `<input>`, `<textarea>`, or
 * contentEditable element is focused — so on-screen forms (PIN entry,
 * pairing code) work normally.
 */

const SCAN_GAP_MS = 80;
const SCAN_MIN_LENGTH = 3;

const isPrintable = (key: string): boolean => key.length === 1;
// Enter only — Tab is a navigation key for screen readers and admin
// keyboard users; treating it as a scan terminator could fire onScan
// with a partial buffer when no input is focused.
const isTerminator = (key: string): boolean => key === "Enter";

const focusIsInput = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  return false;
};

export const useBarcodeScanner = (
  onScan: (text: string) => void,
  enabled = true
): void => {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return;
    let buf = "";
    let lastAt = 0;

    const handler = (e: KeyboardEvent) => {
      if (focusIsInput(e.target)) return;

      const now = performance.now();
      const gap = now - lastAt;

      if (isTerminator(e.key)) {
        if (buf.length >= SCAN_MIN_LENGTH) {
          e.preventDefault();
          const scanned = buf;
          buf = "";
          onScanRef.current(scanned);
        } else {
          buf = "";
        }
        return;
      }

      if (!isPrintable(e.key)) return;

      // Reset buffer if this keystroke is too far from the previous one
      // (i.e. probably a fresh human keystroke after a break).
      if (gap > SCAN_GAP_MS && buf.length > 0) {
        buf = "";
      }
      buf += e.key;
      lastAt = now;
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled]);
};

/**
 * Tracks whether the scanner is currently suspended because an input,
 * textarea, select, or contentEditable element holds focus. Use to show
 * an operator-visible "scanner paused" indicator — without it scans land
 * silently in the focused field with no feedback that the read was eaten.
 */
export const useScannerPausedByFocus = (enabled = true): boolean => {
  // Lazy initializer so the first render already reflects DOM state — avoids
  // a one-frame flash where the indicator says "scanning" while focus is
  // actually parked in a field.
  const [paused, setPaused] = useState<boolean>(() =>
    typeof document !== "undefined"
      ? focusIsInput(document.activeElement)
      : false
  );

  useEffect(() => {
    if (!enabled) {
      setPaused(false);
      return;
    }
    const check = () => setPaused(focusIsInput(document.activeElement));
    check();
    // focusin/focusout bubble; focus/blur do not. Listening at the document
    // level catches focus moving anywhere — including into Mantine modals,
    // which portal into document.body but remain in the same focus tree.
    document.addEventListener("focusin", check);
    document.addEventListener("focusout", check);
    return () => {
      document.removeEventListener("focusin", check);
      document.removeEventListener("focusout", check);
    };
  }, [enabled]);

  return paused;
};

/**
 * Heartbeat hook. Pings POST /kiosk/auth/heartbeat every `intervalMs` (30s
 * default). On revoked response, calls `onRevoked` so the parent can
 * redirect to /pair.
 */
export const useHeartbeat = (
  ping: () => Promise<{ revoked: boolean; serverTime: number }>,
  onRevoked: () => void,
  intervalMs = 30_000
): void => {
  useEffect(() => {
    let cancelled = false;
    // Single-flight guard: a slow ping (network blip, server cold start)
    // used to overlap with the next interval tick, producing concurrent
    // POST /kiosk/auth/heartbeat calls and the resulting
    // TransactionConflict on the KioskDevice row. Skip the tick if one is
    // still in flight rather than queueing it — heartbeats are advisory
    // and a missed beat is irrelevant.
    let inFlight = false;
    const tick = async () => {
      if (inFlight) {
        // Debug-only — a skipped tick is normal when the previous ping
        // is still in flight (slow network, server cold start). Logged
        // so a future "kiosk shows offline but server logs heartbeats"
        // investigation has a footprint to grep for.
        console.debug("[kiosk] heartbeat tick skipped — previous still in flight");
        return;
      }
      inFlight = true;
      try {
        const r = await ping();
        if (cancelled) return;
        if (r.revoked) onRevoked();
      } catch {
        // Network blip — try again next tick. Persistent failure is the
        // user's problem to notice via the offline indicator.
      } finally {
        inFlight = false;
      }
    };
    void tick();
    const id = window.setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [ping, onRevoked, intervalMs]);
};

/**
 * Discriminate a scanned string between a printed badge code (12-char
 * alphanumeric, our specific alphabet) and a rotating QR payload (contains
 * dots — UUIDs separated by `.`).
 */
import { KIOSK_QR_PAYLOAD_REGEX } from "@medicoach/api-utils/kiosk";

const BADGE_CODE_REGEX = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{12}$/;
// Shared with the server, web and mobile via @medicoach/api-utils/kiosk.
// Accepts both legacy unprefixed payloads and the v1.-prefixed payloads
// new clients emit.
const QR_PAYLOAD_REGEX = KIOSK_QR_PAYLOAD_REGEX;

export type ScanShape =
  | { kind: "badge"; badgeCode: string }
  | { kind: "qr"; qrPayload: string }
  | { kind: "barcode"; sku: string };

export const classifyScan = (raw: string): ScanShape => {
  const trimmed = raw.trim();
  if (BADGE_CODE_REGEX.test(trimmed)) {
    return { kind: "badge", badgeCode: trimmed };
  }
  if (QR_PAYLOAD_REGEX.test(trimmed)) {
    return { kind: "qr", qrPayload: trimmed };
  }
  return { kind: "barcode", sku: trimmed };
};
